const VIDEO_CAPTURE_TIMEOUT_MS = 10000;
const VIDEO_CAPTURE_FETCH_TIMEOUT_MS = 60000;

const waitForVideoEvent = (
  video: HTMLVideoElement,
  eventName: 'loadedmetadata' | 'loadeddata' | 'seeked',
): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for video ${eventName}`));
    }, VIDEO_CAPTURE_TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      video.removeEventListener(eventName, handleEvent);
      video.removeEventListener('error', handleError);
    };

    const handleEvent = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      const mediaError = video.error;
      reject(
        new Error(
          mediaError?.message
            ? `Failed to load video for capture (${mediaError.message})`
            : 'Failed to load video for capture',
        ),
      );
    };

    video.addEventListener(eventName, handleEvent, { once: true });
    video.addEventListener('error', handleError, { once: true });
  });

const seekVideoToTime = async (video: HTMLVideoElement, currentTime: number) => {
  const maxSeekTime = Number.isFinite(video.duration)
    ? Math.max(video.duration - 0.001, 0)
    : currentTime;
  const targetTime = Math.min(Math.max(currentTime, 0), maxSeekTime);
  if (Math.abs(video.currentTime - targetTime) > 0.01) {
    const seeked = waitForVideoEvent(video, 'seeked');
    video.currentTime = targetTime;
    await seeked;
  } else if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await waitForVideoEvent(video, 'loadeddata');
  }
};

const isUnusableCaptureBody = (contentType: string | null | undefined) => {
  const type = (contentType || '').toLowerCase();
  return type.includes('application/json') || type.startsWith('text/');
};

/**
 * Drop a temporary capture ``<video>`` and revoke any blob URL it owns.
 * Safe to call twice. ``load()`` here is intentional: it detaches the resource
 * after we are done, and no error listener is attached.
 */
export function detachCaptureVideo(video: HTMLVideoElement | null | undefined) {
  if (!video) return;
  const objectUrl = video.dataset.captureObjectUrl;
  video.removeAttribute('src');
  try {
    video.load();
  } catch {
    // jsdom does not implement HTMLMediaElement.load.
  }
  video.remove();
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    delete video.dataset.captureObjectUrl;
  }
}

/**
 * Decode one frame of ``src`` (a same-origin ``/api/proxy/…`` URL) for canvas
 * capture. Playback itself uses a cross-origin presigned S3 URL, which taints
 * the canvas; this loader re-fetches the bytes through our origin so
 * ``toDataURL`` can run.
 *
 * We buffer via ``fetch`` + blob URL instead of assigning the proxy URL to
 * ``video.src``. A media element would issue Range requests, and aborting a
 * load with a follow-up ``video.load()`` (or a proxy that mishandles Range)
 * fires ``error`` — the failure mode behind "Failed to load video for capture"
 * on crop-release. ``fetch`` is a single GET, the same path images already use.
 */
export async function loadSameOriginVideoFrame(
  src: string,
  currentTime: number,
): Promise<HTMLVideoElement> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    VIDEO_CAPTURE_FETCH_TIMEOUT_MS,
  );

  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('aria-hidden', 'true');
  // Keep the element in the viewport at a non-zero size. Chrome will not
  // decode frames for a 1×1 / opacity-0 video, which left crop previews blank.
  video.style.position = 'fixed';
  video.style.left = '0';
  video.style.top = '0';
  video.style.width = '16px';
  video.style.height = '16px';
  video.style.opacity = '0.01';
  video.style.pointerEvents = 'none';
  video.style.zIndex = '-1';
  document.body.appendChild(video);

  try {
    const response = await fetch(src, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to load video for capture (${response.status})`);
    }
    if (isUnusableCaptureBody(response.headers.get('content-type'))) {
      throw new Error('Failed to load video for capture');
    }

    const blob = await response.blob();
    if (isUnusableCaptureBody(blob.type)) {
      throw new Error('Failed to load video for capture');
    }

    const objectUrl = URL.createObjectURL(blob);
    video.dataset.captureObjectUrl = objectUrl;

    const metadataLoaded = waitForVideoEvent(video, 'loadedmetadata');
    video.src = objectUrl;
    await metadataLoaded;
    if (video.videoWidth && video.videoHeight) {
      video.style.width = `${video.videoWidth}px`;
      video.style.height = `${video.videoHeight}px`;
    }
    await seekVideoToTime(video, currentTime);
    try {
      await video.play();
      video.pause();
      await seekVideoToTime(video, currentTime);
    } catch {
      // Autoplay may still be blocked; seeked data is the fallback.
    }
    await Promise.race([
      new Promise<void>((resolve) => {
        const requestFrame = (
          video as HTMLVideoElement & {
            requestVideoFrameCallback?: (cb: () => void) => number;
          }
        ).requestVideoFrameCallback;
        if (typeof requestFrame === 'function') {
          requestFrame.call(video, () => resolve());
          return;
        }
        requestAnimationFrame(() => resolve());
      }),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, 500);
      }),
    ]);
    return video;
  } catch (error) {
    detachCaptureVideo(video);
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}
